import {Component, inject, OnDestroy} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {Subject, debounceTime, distinctUntilChanged, switchMap, of} from 'rxjs';
import {DataService} from '../services/data.service';

export interface SearchResult {
    id: number;
    name: string;
    email?: string;
    phone?: string;
    type: string;
    type_label: string;
    route: string;
    street?: string;
    city?: string;
    country?: string;
}

@Component({
    selector: 'app-global-search',
    imports: [CommonModule, FormsModule],
    styles: `
        .global-search {
            width: 300px;
        }

        .search-result-item:hover,
        .search-result-item.hover {
            background-color: #eee !important;
        }

        .search-results {
            border-top: none !important;
            max-height: 400px;
            overflow-y: auto;
        }

        .cursor-pointer {
            cursor: pointer;
        }

        @media (max-width: 768px) {
            .global-search {
                width: 250px;
            }
        }

        @media (max-width: 576px) {
            .global-search {
                width: 200px;
            }
        }

        .input-group-text {
            background-color: var(--bs-body-bg);
            border-right: none;
            color: var(--bs-body-color);
        }

        [data-bs-theme="dark"] .input-group-text {
            background-color: var(--bs-dark);
            border-color: var(--bs-gray-700);
        }

        [data-bs-theme="dark"] .form-control {
            background-color: var(--bs-dark);
            border-color: var(--bs-gray-700);
            color: var(--bs-body-color);
        }

        [data-bs-theme="dark"] .form-control::placeholder {
            color: var(--bs-gray-500);
            opacity: 1;
        }

        .form-control:focus {
            border-color: #86b7fe;
            box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
        }

        .search-results {
            border: 1px solid var(--bs-border-color);
            background-color: var(--bs-body-bg);
            color: var(--bs-body-color);
        }

        [data-bs-theme="dark"] .search-results {
            background-color: var(--bs-dark);
            border-color: var(--bs-gray-700);
        }

        .search-result-item:last-child {
            border-bottom: none !important;
        }
    `,
    template: `
        <div class="global-search position-relative">
            <div class="input-group">
                <span class="input-group-text">
                    <i class="fa fa-search"></i>
                </span>
                <input type="text" class="form-control" placeholder="Suchen..."
                    [(ngModel)]="searchQuery"
                    (input)="onSearchInput()"
                    (focus)="showResults = true"
                    (blur)="onBlur()"
                    #searchInput>
                @if (searchQuery) {
                    <button class="btn btn-outline-secondary" type="button" (click)="clearSearch()">
                        <i class="fa fa-times"></i>
                    </button>
                }
            </div>

            <div
                class="search-results position-absolute w-100 bg-white border rounded-bottom shadow-sm mt-1"
                *ngIf="showResults && (searchResults.length > 0 || (searchQuery && searchQuery.length >= 2))"
                style="z-index: 1050; max-height: 400px; overflow-y: auto;">

                <div *ngIf="loading" class="p-3 text-center text-muted bg-light">
                    <i class="fa fa-spinner fa-spin"></i> Suche läuft...
                </div>

                <div *ngIf="!loading && searchResults.length === 0 && searchQuery && searchQuery.length >= 2"
                     class="p-3 text-muted text-center bg-light">
                    Keine Ergebnisse für "{{ searchQuery }}" gefunden
                </div>

                <div *ngIf="!loading && searchResults.length > 0">
                    <div class="p-2 bg-light border-bottom">
                        <small class="text-muted">{{ searchResults.length }} Ergebnis(se) gefunden</small>
                    </div>

                    <div
                        *ngFor="let result of searchResults; trackBy: trackByResult"
                        class="search-result-item p-3 border-bottom cursor-pointer bg-light"
                        (mousedown)="selectResult(result)"
                        [class.hover]="hoveredIndex === result.id">

                        <div class="d-flex align-items-center">
                            <div class="flex-shrink-0 me-3">
                        <span class="badge" [ngClass]="getTypeBadgeClass(result.type)">
                          {{ result.type_label }}
                        </span>
                            </div>

                            <div class="flex-grow-1">
                                <div class="fw-bold text-dark">{{ result.name }}</div>
                                <div class="text-muted small" *ngIf="getSubtext(result)">
                                    {{ getSubtext(result) }}
                                </div>
                            </div>

                            <div class="flex-shrink-0">
                                <i class="fa fa-arrow-right text-muted"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `
})
export class GlobalSearchComponent implements OnDestroy {

    private dataService = inject(DataService);
    private router = inject(Router);

    searchQuery = '';
    searchResults: SearchResult[] = [];
    loading = false;
    showResults = false;
    hoveredIndex: number | null = null;

    private searchSubject = new Subject<string>();
    private destroy$ = new Subject<void>();

    constructor() {
        this.searchSubject.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            switchMap(query => {
                if (!query || query.length < 2) {
                    this.searchResults = [];
                    this.loading = false;
                    return of(null);
                }

                this.loading = true;
                return this.dataService.request('Search/search', {query, limit: 15});
            })
        ).subscribe(response => {
            this.loading = false;
            if (response && response.results) {
                this.searchResults = response.results;
            } else {
                this.searchResults = [];
            }
        });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onSearchInput() {
        this.searchSubject.next(this.searchQuery);
    }

    onBlur() {
        setTimeout(() => {
            this.showResults = false;
        }, 150);
    }

    clearSearch() {
        this.searchQuery = '';
        this.searchResults = [];
        this.showResults = false;
    }

    selectResult(result: SearchResult) {
        this.router.navigate([result.route]);
        this.clearSearch();
    }

    trackByResult(index: number, result: SearchResult): any {
        return result.id + '-' + result.type;
    }

    getTypeBadgeClass(type: string): string {
        const classes = {
            'store': 'text-bg-primary',
            'franchisor': 'text-bg-success',
            'franchisee': 'text-bg-info',
            'user': 'text-bg-warning',
            'region': 'text-bg-secondary'
        };
        return classes[type as keyof typeof classes] || 'text-bg-secondary';
    }

    getSubtext(result: SearchResult): string {
        const parts = [];

        if (result.city) parts.push(result.city);
        if (result.country) parts.push(result.country);
        if (result.email) parts.push(result.email);

        return parts.join(', ');
    }
}
